/* storybook-check-ignore */
import React, { useEffect } from 'react';
import Head from 'next/head';
import dynamic from 'next/dynamic';

import GlobalStyles from '../../shared/GlobalStyles';
import { Props as HeaderProps } from '../layout/Header';
import * as WordpressHeadGlobal from '../../WordpressPorts';
import ReduxWrapper from '../../shared/ReduxWrapper';
import { Box, BoxProps } from '@opendoor/bricks/core';
import { ThemedLite } from '@opendoor/bricks/theme/ODThemeLite';
import { exclusives } from '@opendoor/bricks/theme';
import { media } from '@opendoor/bricks/theme/legacy/foundations/mediaBreakpoints';
import { eero } from '@opendoor/bricks/theme/eero';
import { ITheme } from '@opendoor/bricks/system/theme';
import Headerv2, { HeaderV2Props } from 'components/exclusives/layout/Headerv2/Headerv2';
import { Routes } from 'components/exclusives/constants/routes';
import isUndefined from 'lodash/isUndefined';

const FrontChat = dynamic(() => import('../layout/FrontChat'), {
  ssr: false,
});
import MarketSelectorModal from '../MarketSelectorModal';
import { HotjarScript } from 'components/exclusives/scripts/HotjarScript';
import { MetaPixelScript } from 'components/exclusives/scripts/MetaPixelScript';
import { FrontChatProps } from '../layout/FrontChat';

const assetPrefix = 'https://cdn.opendoor.com/fonts';

const GlobalCss = `
@font-face {
  font-family: 'Publico, serif';
  src: url('${assetPrefix}/PublicoHeadline-Bold-Web.eot?#iefix')
      format('embedded-opentype'),
    url('${assetPrefix}/PublicoHeadline-Bold-Web.woff') format('woff'),
    url('${assetPrefix}/PublicoHeadline-Bold-Web.ttf') format('truetype'),
    url('${assetPrefix}/PublicoHeadline-Bold-Web.svg#PublicoHeadline-Bold-Web') format('svg');
  font-weight: 600;
  font-style: bolder;
}

body {
  background-color: neutrals100;
}

input[type=checkbox] {
  accent-color: var(--colors-brand50);
}
:root {
  --doc-height: 100vh;
  --header-max-width: 82rem;
  --content-max-width: 82rem;
  --content-narrow-max-width: 82rem;
  --content-full-max-width: 100rem;
}

html,
body {
 padding: 0;
 margin: 0;
 height: 100vh; /* fallback for Js load */
 height: var(--doc-height);
}

${media.largerThan.LG} {
  :root {
    --header-max-width: max(82rem, 82vw);
    --content-max-width: max(82rem, 82vw);
    --content-narrow-max-width: max(82rem, 96vw);
  }
}

${media.largerThan.XL} {
  :root {
    --header-max-width: max(82rem, 82vw);
    --content-max-width: max(82rem, 82vw);
    --content-narrow-max-width: max(82rem, 96vw);
  }
}

${media.largerThan.XXL} {
  :root {
    --content-narrow-max-width: max(82rem, 96vw);
  }
}
`;

interface PageProps extends FrontChatProps {
  region?: string | null;
  hideHeaderNavLinks?: boolean;
  title?: string;
  description?: string;
  logoToGallery?: boolean;
  headerMaxWidth?: BoxProps['maxWidth'];
  openLinkOnNewTab?: boolean;
  shouldShowSearch?: boolean;
  containerProps?: BoxProps;
  shouldUseEeroBreakpoints?: boolean;
  /**
   * @TODO: Remove `HeaderProps` once we've turned off the
   * feature flag and migrated to `HeaderV2Props`
   */
  headerProps?: HeaderProps &
    Pick<
      HeaderV2Props,
      'logoHref' | 'isFullWidth' | 'isNavigationHidden' | 'isSearchHidden' | 'maxWidth'
    >;
  Header?: React.ReactNode;
  loadHotjar?: boolean;
  breakpoints?: ITheme['breakpoints'];
}

const DefaultLayout: React.FC<PageProps & BoxProps> = ({
  breakpoints,
  children,
  containerProps,
  description,
  Header,
  headerMaxWidth,
  headerProps,
  hideFrontChat,
  hideBubble,
  hideHeaderNavLinks,
  loadHotjar,
  logoToGallery,
  openLinkOnNewTab,
  region,
  title = 'Opendoor',
  shouldShowSearch,
  shouldUseEeroBreakpoints,
  ...props
}) => {
  useEffect(() => {
    if (!document) {
      return;
    }

    const documentHeight = () => {
      const doc = document.documentElement;
      doc.style.setProperty('--doc-height', `${window.innerHeight}px`);
    };
    window.addEventListener('resize', documentHeight);
    documentHeight();
    return () => window.removeEventListener('resize', documentHeight);
  }, []);

  return (
    <ReduxWrapper>
      <>
        <Head>
          <title>{title}</title>
          <meta key="og:title" property="og:title" content={title} />
          <meta key="twitter:title" name="twitter:title" content={title} />
          {description && (
            <>
              <meta key="description" name="description" content={description} />
              <meta key="og:description" property="og:description" content={description} />
              <meta key="twitter:description" name="twitter:description" content={description} />
            </>
          )}
          <meta
            key="og:image"
            property="og:image"
            content="https://imgdrop.imgix.net/0de83246f5284584b22165a7cb562cb7.png"
          />
          <meta property="og:image:width" content="4534" />
          <meta property="og:image:height" content="2551" />
          <link
            rel="preload"
            crossOrigin="anonymous"
            href={`${assetPrefix}/PublicoHeadline-Bold-Web.woff2`}
            as="font"
          />
          <style dangerouslySetInnerHTML={{ __html: GlobalCss }} />
          {loadHotjar && <HotjarScript />}
          <MetaPixelScript />
          <noscript>
            <img
              alt=""
              height="1"
              width="1"
              style={{ display: 'none' }}
              src="https://www.facebook.com/tr?id=1610195155916409&ev=PageView&noscript=1"
            />
          </noscript>
        </Head>
        <WordpressHeadGlobal.Page>
          <ThemedLite
            theme={{
              ...exclusives,
              breakpoints: shouldUseEeroBreakpoints ? eero.breakpoints : exclusives.breakpoints,
              ...(breakpoints && { breakpoints }),
            }}
          >
            <GlobalStyles />
            {!isUndefined(Header) ? (
              Header
            ) : (
              <Headerv2
                /**
                 * @TODO: Remove `logoToGallery` prop once we remove the flag and spread `headerProps` instead.
                 */
                logoHref={headerProps?.logoHref ?? logoToGallery ? Routes.GALLERY : Routes.ROOT}
                isFullWidth={headerProps?.isFullWidth}
                isNavigationHidden={headerProps?.isNavigationHidden}
                isSearchHidden={headerProps?.isSearchHidden}
                maxWidth={headerProps?.maxWidth}
              />
            )}
            <Box {...props} {...containerProps}>
              {children}
            </Box>
            <MarketSelectorModal />
          </ThemedLite>
        </WordpressHeadGlobal.Page>
        <FrontChat hideFrontChat={hideFrontChat} hideBubble={hideBubble} />
      </>
    </ReduxWrapper>
  );
};

export default DefaultLayout;
